import { useEffect, useState } from 'react';
import env from '../../config/env';

function SelectedEmplacementDeliveryInput({ handleSelectLocation, resetInput }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [show, setShow] = useState(false);

  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Session token pour Search Box (important)
  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());

  const MAPBOX_TOKEN = env.mapboxToken;

  useEffect(() => {
    if (resetInput) setSearchTerm('');
  }, [resetInput]);

  // --- 1) Position actuelle ---
  const handleSelectCurrentPosition = () => {
    if (!navigator.geolocation) {
      setError(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lng = pos.coords.longitude;
        const lat = pos.coords.latitude;

        const suggestion = {
          kind: 'current_location',
          place_name: 'Position actuelle',
          center: [lng, lat],
        };

        setSearchResults([]);
        setSearchTerm('Position actuelle');
        handleSelectLocation(suggestion);
        setShow(false);
      },
      (e) => setError(e),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // --- 2) Click item (POI ou place ou current) ---
  const handleSelectLocationInput = async (item) => {
    try {
      setError(null);

      // Current location
      if (item.kind === 'current_location') {
        handleSelectCurrentPosition();
        return;
      }

      // POI : on retrieve pour coords
      if (item.kind === 'poi') {
        const url =
          `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(
            item.mapbox_id
          )}` +
          `?access_token=${MAPBOX_TOKEN}` +
          `&session_token=${sessionToken}` +
          `&language=fr`;

        const res = await fetch(url);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        const feature = data?.features?.[0];

        const normalized = {
          ...item,
          place_name:
            feature?.properties?.full_address || feature?.properties?.name || item.place_name,
          center: feature?.geometry?.coordinates,
          geometry: feature?.geometry,
          properties: feature?.properties,
        };

        setSearchResults([]);
        setSearchTerm(normalized.place_name);
        handleSelectLocation(normalized);

        // nouvelle session après un choix
        // setSessionToken(crypto.randomUUID());
        setShow(false);
        return;
      }

      // Place (ville) : geocoding feature déjà OK
      if (item.kind === 'place') {
        setSearchResults([]);
        setSearchTerm(item.place_name);
        handleSelectLocation(item);
        setShow(false);
        return;
      }
    } catch (e) {
      setError(e);
    }
  };

  // --- 3) Input change : fetch POI transport + fetch place fallback ---
  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    setSearchTerm(inputValue);

    if (inputValue.length >= 1) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          // A) POI transport (Search Box)
          const poiUrl =
            `https://api.mapbox.com/search/searchbox/v1/suggest` +
            `?q=${encodeURIComponent(inputValue)}` +
            `&access_token=${MAPBOX_TOKEN}` +
            `&session_token=${sessionToken}` +
            `&language=fr&country=FR&limit=8` +
            `&types=poi` +
            // Filtrage "Getaround-like"
            `&poi_category=${encodeURIComponent(
              'airport,train_station,transit_station,bus_station'
            )}`;

          // B) Ville fallback (Geocoding v5)
          const placeUrl =
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              inputValue
            )}.json` +
            `?access_token=${MAPBOX_TOKEN}` +
            `&language=fr&country=fr` +
            `&types=place&limit=3`;

          const [poiRes, placeRes] = await Promise.all([fetch(poiUrl), fetch(placeUrl)]);

          if (!poiRes.ok || !placeRes.ok) {
            throw new Error('Network response was not ok');
          }

          const poiData = await poiRes.json();
          const placeData = await placeRes.json();

          // 1) “Position actuelle” toujours en haut (comme Getaround)
          const items = [
            {
              kind: 'current_location',
              place_name: 'Position actuelle',
              icon: 'navigate-outline',
            },
          ];

          // 2) POI transport
          const poiItems = (poiData?.suggestions ?? []).map((s) => ({
            kind: 'poi',
            mapbox_id: s.mapbox_id,
            place_name: s.name + (s.place_formatted ? `, ${s.place_formatted}` : ''),
            icon: s.name?.toLowerCase().includes('aéroport') ? 'airplane-outline' : 'train-outline',
          }));

          // 3) Places (villes) fallback
          const placeItems = (placeData?.features ?? []).map((f) => ({
            kind: 'place',
            ...f,
            place_name: f.place_name,
            icon: 'business-outline',
          }));

          setSearchResults([...items, ...poiItems, ...placeItems]);
          setShow(true);
        } catch (e) {
          setError(e);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setSearchResults([]);
      setShow(false);
    }
  };

  const showMenu = searchTerm.length >= 2 && show;

  return (
    <div className="mc-autocomplete">
      <label className="mc-field-label" htmlFor="categoryProduct">
        Adresse d'emplacement <sup>*</sup>
      </label>

      <div className="mc-autocomplete-field">
        <ion-icon name="location-outline"></ion-icon>
        <input
          type="text"
          className="mc-input"
          id="categoryProduct"
          required
          name="categoryProduct"
          onChange={handleInputChange}
          placeholder="Ville, gare ou aéroport"
          value={searchTerm}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showMenu}
          onBlur={() => setTimeout(() => setShow(false), 200)}
          onFocus={() => setShow(true)}
        />
      </div>

      {showMenu && (
        <ul className="mc-autocomplete-menu">
          {loading && <li className="mc-autocomplete-state">Recherche en cours…</li>}

          {!loading && error && (
            <li className="mc-autocomplete-state mc-autocomplete-state--error">
              Erreur de recherche. Réessayez dans un instant.
            </li>
          )}

          {!loading && !error && searchResults.length === 0 && (
            <li className="mc-autocomplete-state">Aucun résultat</li>
          )}

          {!loading &&
            !error &&
            searchResults.map((item, index) => (
              <li key={index}>
                <button
                  className={`mc-autocomplete-item${
                    item.kind === 'current_location' ? ' mc-autocomplete-item--current' : ''
                  }`}
                  type="button"
                  onClick={() => handleSelectLocationInput(item)}
                >
                  <ion-icon name={item.icon || 'location-outline'}></ion-icon>
                  <span>{item.place_name}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export default SelectedEmplacementDeliveryInput;
